import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { DailyOrderReport, SalesSummaryReport, FinancialSettlementReport, OrderDateRangeRequest, AllDayCheckDto } from '../../types';
import { apiService } from '../../services/apiService';
import { showErrorMessage } from './uiSlice';

interface ReportsState {
  dailyReports: DailyOrderReport[];
  salesSummaryReport: SalesSummaryReport | null;
  financialSettlementReport: FinancialSettlementReport | null;
  allDayChecks: AllDayCheckDto[];
  loading: boolean;
  error: string | null;
}

const initialState: ReportsState = {
  dailyReports: [],
  salesSummaryReport: null,
  financialSettlementReport: null,
  allDayChecks: [],
  loading: false,
  error: null
};

export const fetchDailyOrderReports = createAsyncThunk(
  'reports/fetchDailyOrderReports',
  async (request: OrderDateRangeRequest, { rejectWithValue, dispatch }) => {
    try {
      return await apiService.getDailyOrderReports(request);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch daily order reports';
      dispatch(showErrorMessage(errorMessage));
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchSalesSummaryReport = createAsyncThunk(
  'reports/fetchSalesSummaryReport',
  async (request: OrderDateRangeRequest, { rejectWithValue, dispatch }) => {
    try {
      return await apiService.getSalesSummaryReport(request);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch sales summary report';
      dispatch(showErrorMessage(errorMessage));
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchFinancialSettlementReport = createAsyncThunk(
  'reports/fetchFinancialSettlementReport',
  async (request: OrderDateRangeRequest, { rejectWithValue, dispatch }) => {
    try {
      return await apiService.getFinancialSettlementReport(request);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch financial settlement report';
      dispatch(showErrorMessage(errorMessage));
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchAllDayChecks = createAsyncThunk(
  'reports/fetchAllDayChecks',
  async (request: OrderDateRangeRequest, { rejectWithValue, dispatch }) => {
    try {
      return await apiService.getAllDayChecks(request);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch all day checks report';
      dispatch(showErrorMessage(errorMessage));
      return rejectWithValue(errorMessage);
    }
  }
);

// Monthly reports endpoint removed

const reportsSlice = createSlice({
  name: 'reports',
  initialState,
  reducers: {
    clearReports: (state) => {
      state.dailyReports = [];
      state.salesSummaryReport = null;
      state.financialSettlementReport = null;
      state.allDayChecks = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Daily reports
      .addCase(fetchDailyOrderReports.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDailyOrderReports.fulfilled, (state, action) => {
        state.loading = false;
        state.dailyReports = action.payload;
      })
      .addCase(fetchDailyOrderReports.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Sales summary reports
      .addCase(fetchSalesSummaryReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSalesSummaryReport.fulfilled, (state, action) => {
        state.loading = false;
        state.salesSummaryReport = action.payload;
      })
      .addCase(fetchSalesSummaryReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Financial settlement reports
      .addCase(fetchFinancialSettlementReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFinancialSettlementReport.fulfilled, (state, action) => {
        state.loading = false;
        state.financialSettlementReport = action.payload;
      })
      .addCase(fetchFinancialSettlementReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // All day checks
      .addCase(fetchAllDayChecks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllDayChecks.fulfilled, (state, action) => {
        state.loading = false;
        state.allDayChecks = action.payload;
      })
      .addCase(fetchAllDayChecks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  }
});

export const { clearReports } = reportsSlice.actions;

export default reportsSlice.reducer;
